// src/components/home/FeaturedProgramsSection.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaChevronRight, 
  FaBuilding, 
  FaRulerCombined, 
  FaMap, 
  FaDesktop, 
  FaCode,
  FaGlobe,
  FaHelicopter,
  FaCalculator,
  FaLeaf,
  FaRecycle,
  FaRobot,
  FaPencilRuler
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { 
  DIGITAL_COURSES,
  ENGINEERING_COURSES,
  GREEN_COURSES
} from '../../data/coursesdata';
import Section from '../common/Section';
import Heading from '../common/Heading';
import Button from '../common/Button';

const ProgramCard = ({ course }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Get appropriate icon for each program
  const getIcon = () => {
    if (course.icon && React.isValidElement(course.icon)) {
      return React.cloneElement(course.icon, { className: "h-8 w-8" });
    }
    
    // Fallback icons based on category or ID
    if (course.category === 'engineering') {
      if (course.id === 'bim' || course.id === 'revit-architecture' || course.id === 'chief-architect') {
        return <FaBuilding className="h-8 w-8" />;
      }
      if (course.id === 'cad' || course.id === 'autocad') {
        return <FaRulerCombined className="h-8 w-8" />;
      }
      if (course.id.includes('gis') || course.id.includes('survey')) {
        return <FaMap className="h-8 w-8" />;
      }
      if (course.id.includes('drone')) {
        return <FaHelicopter className="h-8 w-8" />;
      }
      if (course.id === 'bill-of-quantities') {
        return <FaCalculator className="h-8 w-8" />;
      }
      return <FaRulerCombined className="h-8 w-8" />;
    }
    
    if (course.category === 'digital') {
      if (course.id === 'web-design') {
        return <FaGlobe className="h-8 w-8" />;
      }
      if (course.id === 'ai-literacy') {
        return <FaRobot className="h-8 w-8" />;
      }
      if (course.id === 'graphics-design') {
        return <FaPencilRuler className="h-8 w-8" />;
      }
      return <FaDesktop className="h-8 w-8" />;
    }
    
    if (course.category === 'green') {
      if (course.id === 'waste-to-value') {
        return <FaRecycle className="h-8 w-8" />;
      }
      return <FaLeaf className="h-8 w-8" />;
    }
    
    return <FaDesktop className="h-8 w-8" />;
  };
  
  const cardVariants = {
    hover: {
      y: -10,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    initial: {
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };
  
  return (
    <motion.div
      className="bg-white rounded-lg shadow-lg overflow-hidden h-full"
      variants={cardVariants}
      initial="initial"
      animate={isHovered ? "hover" : "initial"}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bg-primary text-white p-6 flex items-center justify-center">
        {getIcon()}
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-primary-dark mb-3">{course.title}</h3>
        <p className="text-gray-600 mb-6">{course.shortDesc}</p>
        
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Key Features:</h4>
          <ul className="space-y-1">
            {course.features && course.features.slice(0, 4).map((feature, i) => (
              <li key={i} className="flex items-center text-gray-600">
                <span className="text-secondary mr-2">•</span>
                {feature}
              </li>
            ))}
          </ul>
        </div>
        
        <Link
          to={`/programs/${course.id}`}
          className="inline-flex items-center text-primary font-medium hover:text-primary-dark transition-colors"
        >
          Learn More <FaChevronRight className="ml-2 h-3 w-3" />
        </Link>
      </div>
    </motion.div>
  );
};

const FeaturedProgramsSection = () => {
  // Container animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  // Item animation variants
  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  
  // Select featured courses from different categories
  const getFeaturedCourses = () => {
    const featured = [];
    
    // Add engineering courses
    if (ENGINEERING_COURSES.courses && ENGINEERING_COURSES.courses.length > 0) {
      // Add main BIM course
      const bimCourse = ENGINEERING_COURSES.courses.find(course => course.id === 'bim');
      if (bimCourse) {
        featured.push(bimCourse);
      }
      
      // Add CAD course
      const cadCourse = ENGINEERING_COURSES.courses.find(course => course.id === 'cad');
      if (cadCourse) {
        featured.push(cadCourse);
      }
    }
    
    // Add digital courses
    if (DIGITAL_COURSES.courses && DIGITAL_COURSES.courses.length > 0) {
      // Add web design course
      const webDesignCourse = DIGITAL_COURSES.courses.find(course => course.id === 'web-design');
      if (webDesignCourse) {
        featured.push(webDesignCourse);
      }
      
      // Add AI literacy course as an alternative
      if (featured.length < 3) {
        const aiCourse = DIGITAL_COURSES.courses.find(course => course.id === 'ai-literacy');
        if (aiCourse) {
          featured.push(aiCourse);
        }
      }
    }
    
    // Add green courses if needed
    if (featured.length < 3 && GREEN_COURSES.courses && GREEN_COURSES.courses.length > 0) {
      const greenCourse = GREEN_COURSES.courses[0];
      featured.push(greenCourse);
    }
    
    // Fill remaining slots with any available courses
    if (featured.length < 3) {
      const allCourses = [
        ...(ENGINEERING_COURSES.courses || []),
        ...(DIGITAL_COURSES.courses || []),
        ...(GREEN_COURSES.courses || [])
      ];
      
      // Add any other courses not already included
      for (const course of allCourses) {
        if (featured.length >= 3) break;
        if (!featured.find(c => c.id === course.id)) {
          featured.push(course);
        }
      }
    }
    
    return featured;
  };
  
  const featuredCourses = getFeaturedCourses();
  
  return (
    <Section background="gray" className="overflow-hidden">
      <div className="text-center mb-12">
        <Heading level={2} align="center" className="mb-4" withAccent>
          Our Featured Programs
        </Heading>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Practical, hands-on training in cutting-edge digital skills for the modern workforce
        </p>
      </div>
      
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {featuredCourses.map((course, index) => (
          <motion.div key={course.id} variants={itemVariants}>
            <ProgramCard course={course} />
          </motion.div>
        ))}
      </motion.div>
      
      <div className="text-center mt-12">
        <Button 
          variant="primary" 
          size="lg" 
          to="/programs"
          icon={<FaChevronRight />}
        >
          View All Programs
        </Button>
      </div>
    </Section>
  );
};

export default FeaturedProgramsSection;